
import { useState } from "react";
import { useParams } from "react-router-dom";
import NotFoundPage from "@/pages/NotFoundPage";
import { motion } from "framer-motion";
import { Heart, ShoppingBag, Star } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { useAppStore } from "@/lib/store";
import { getCategory } from "@/lib/data/categories";
import { cn, conditionLabels, formatPrice } from "@/lib/utils";
import ProductCard from "@/components/product/ProductCard";
import ImageGallery from "@/components/product/ImageGallery";
import RevealOnScroll from "@/components/ui/RevealOnScroll";
import ImmersiveBackground from "@/components/ui/ImmersiveBackground";
import { useDocumentMeta } from "@/lib/useDocumentMeta";

export default function ProductPage() {
  const { id = "" } = useParams<{ id: string }>();
  const products = useAppStore((s) => s.products);
  const product = products.find((p) => p.id === id);

  useDocumentMeta({
    title: product ? product.title : "Listing not found",
    description: product
      ? `${product.title} — ${formatPrice(product.price)}, ${conditionLabels[product.condition]} condition. Sold by ${product.sellerName} on Nerd Multiverse.`
      : "This listing couldn't be found on Nerd Multiverse.",
    path: `/product/${id}`,
    noindex: !product,
  });
  const user = useAppStore((s) => s.currentUser());
  const isFavourited = useAppStore((s) =>
    user ? (s.favourites[user.uid] ?? []).includes(id) : false
  );
  const toggleFavourite = useAppStore((s) => s.toggleFavourite);
  const addToCart = useAppStore((s) => s.addToCart);
  const reviews = useAppStore(
    useShallow((s) => s.reviews.filter((r) => r.productId === id))
  );
  const [added, setAdded] = useState(false);

  if (!product) return <NotFoundPage />;

  const category = getCategory(product.category);
  const related = products
    .filter(
      (p) =>
        p.id !== product.id &&
        p.category === product.category &&
        p.status === "approved"
    )
    .slice(0, 4);

  const handleAddToCart = () => {
    addToCart(product.id);
    setAdded(true);
    setTimeout(() => setAdded(false), 1600);
  };

  const avgRating = reviews.length
    ? reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length
    : null;

  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.title,
    description: product.description,
    category: category?.name,
    ...(product.images.length > 0 && { image: product.images }),
    offers: {
      "@type": "Offer",
      price: product.price,
      priceCurrency: "GBP",
      availability:
        product.status === "sold"
          ? "https://schema.org/SoldOut"
          : "https://schema.org/InStock",
      itemCondition:
        product.condition === "new"
          ? "https://schema.org/NewCondition"
          : "https://schema.org/UsedCondition",
      seller: { "@type": "Person", name: product.sellerName },
    },
    ...(avgRating && {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: avgRating.toFixed(1),
        reviewCount: reviews.length,
      },
    }),
  };

  return (
    <div className="relative pt-28 md:pt-36 pb-24">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productJsonLd) }}
      />
      <ImmersiveBackground
        src="/images/hero/product-hero.webp"
        mobileSrc="/images/hero/product-mobile-v2.webp"
        focus="50% 24%"
      />
      <div className="nm-container grid lg:grid-cols-2 gap-12">
        <RevealOnScroll>
          <div className="relative">
            <ImageGallery
              images={product.images}
              category={product.category}
              title={product.title}
            />
            {product.isRareFind && (
              <span className="absolute top-5 left-5 text-[10px] tracking-[0.15em] uppercase text-white/90 bg-white/10 backdrop-blur-md px-3 py-1.5 rounded-full border border-white/20">
                Rare Find
              </span>
            )}
          </div>
        </RevealOnScroll>

        <RevealOnScroll delay={0.1}>
          <span
            className="text-xs tracking-[0.2em] uppercase"
            style={{ color: category?.accent }}
          >
            {category?.name}
          </span>
          <h1 className="mt-3 font-display text-3xl md:text-4xl font-semibold text-white">
            {product.title}
          </h1>
          <p className="mt-4 text-2xl font-display font-semibold text-white">
            {formatPrice(product.price)}
          </p>

          <div className="mt-6 flex items-center gap-4 text-sm text-[var(--nm-ink-muted)]">
            <span className="px-3 py-1 rounded-full border border-white/15">
              {conditionLabels[product.condition]}
            </span>
            <span>Sold by {product.sellerName}</span>
          </div>

          <p className="mt-6 text-[var(--nm-ink-muted)] leading-relaxed max-w-lg">
            {product.description}
          </p>

          <div className="mt-8 flex gap-3">
            <button
              onClick={handleAddToCart}
              disabled={product.status === "sold"}
              className="flex-1 flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-[#05070d] text-sm font-medium tracking-wide hover:bg-[var(--nm-blue-bright)] hover:text-white transition-colors duration-200 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <motion.span
                animate={added ? { scale: [1, 1.3, 1] } : {}}
                transition={{ duration: 0.35 }}
                className="flex items-center gap-2"
              >
                <ShoppingBag size={16} />
                {product.status === "sold"
                  ? "Sold"
                  : added
                  ? "Added to cart"
                  : "Add to cart"}
              </motion.span>
            </button>
            <button
              onClick={() => user && toggleFavourite(user.uid, product.id)}
              className={cn(
                "w-14 flex items-center justify-center rounded-full border transition-colors",
                isFavourited
                  ? "bg-[var(--nm-pink)] border-[var(--nm-pink)] text-white"
                  : "border-white/20 text-white hover:border-white/50"
              )}
              aria-label="Toggle favourite"
            >
              <Heart size={18} fill={isFavourited ? "white" : "none"} />
            </button>
          </div>

          <div className="mt-10 pt-10 border-t border-white/10 space-y-4 text-sm text-[var(--nm-ink-muted)]">
            <p>Shipped from the UK · Tracked delivery available at checkout.</p>
            <p>All listings are reviewed by the Nerd Multiverse team before going live.</p>
          </div>

          <div className="mt-10 pt-10 border-t border-white/10">
            <h2 className="font-display text-lg font-semibold text-white mb-4">
              Reviews
            </h2>
            {reviews.length === 0 ? (
              <p className="text-sm text-[var(--nm-ink-muted)]">
                No reviews yet for this listing.
              </p>
            ) : (
              <ul className="space-y-4">
                {reviews.map((r) => (
                  <li key={r.id} className="text-sm">
                    <div className="flex items-center gap-2 text-white">
                      <span className="flex items-center gap-0.5 text-[var(--nm-blue-bright)]">
                        {Array.from({ length: r.rating }).map((_, i) => (
                          <Star key={i} size={12} fill="currentColor" />
                        ))}
                      </span>
                      <span>{r.buyerName}</span>
                    </div>
                    <p className="mt-1 text-[var(--nm-ink-muted)]">{r.comment}</p>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </RevealOnScroll>
      </div>

      {related.length > 0 && (
        <div className="nm-container mt-24">
          <h2 className="font-display text-2xl font-semibold text-white mb-6">
            More from {category?.name}
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5">
            {related.map((p) => (
              <ProductCard key={p.id} product={p} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
